/**
 * A plan's head-count limits, and what they mean for the unit steppers.
 * Only units that `includeCapacity` count toward them (e.g. infants on a lap
 * usually don't).
 */

export interface CapacityLimits {
  min?: number;
  max?: number;
}

interface CountedUnit {
  id?: string;
  includeCapacity?: boolean;
}

export type Quantities = ReadonlyMap<string, number>;

/** A translation key and its values, for `UnitsNotice`. */
export interface UnitsMessage {
  key: string;
  values?: Record<string, number>;
}

export const capacityUsed = (units: CountedUnit[], quantities: Quantities) =>
  units.reduce(
    (sum, unit) =>
      unit.includeCapacity ? sum + (quantities.get(unit.id ?? '') ?? 0) : sum,
    0
  );

/**
 * The most this unit's stepper may reach: what the others leave of `max`.
 * Never below the current pick, or a stale pick over the limit could no longer
 * be stepped down. Undefined when nothing caps it.
 */
export const unitMaxQuantity = (
  unit: CountedUnit,
  used: number,
  quantities: Quantities,
  { max }: CapacityLimits
): number | undefined => {
  if (max === undefined || !unit.includeCapacity) return undefined;
  const own = quantities.get(unit.id ?? '') ?? 0;
  return Math.max(max - used + own, own);
};

/**
 * The warning to show above the steppers, if any. Below the minimum comes
 * first: when remaining stock clamps `max` under `min`, that is what explains
 * why booking stays blocked.
 */
export const capacityMessage = (
  units: CountedUnit[],
  used: number,
  { min, max }: CapacityLimits
): UnitsMessage | undefined => {
  // Without a counted unit neither limit can be met or exceeded.
  if (!units.some(unit => unit.includeCapacity)) return undefined;
  if (min && used < min) {
    return {
      key: 'components.unitCard.minCapacityWarning',
      values: { quantity: min }
    };
  }
  if (max && used >= max)
    return { key: 'components.unitCard.maxCapacityReached' };
  return undefined;
};
